import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { uniq, uniqBy } from 'lodash/array';
import { sortBy } from 'lodash/collection';
import * as PropTypes from 'prop-types';

import { StudySiteApi } from '../../../../../api';
import { SSUSelect } from '../../../../SSUFilter/SSUSelect';

import MultiSelectStudyType from './MultiSelectStudyType';

export default function StudySiteSelector({
  onSiteChange,
  onStudyChange,
  onPcnChange,
  studyId,
  siteId,
  pcnFlag = false,
  filteredStudyTypes,
  setFilteredStudyTypes,
  initialStudyTypes,
  isManualStudyTypeSelection,
  setIsManualStudyTypeSelection,
  setInitialStudyTypes
}) {
  const [allStudySites, setAllStudySites] = useState([]);
  const [filteredStudySites, setFilteredStudySites] = useState([]);

  useEffect(
    function() {
      StudySiteApi.getAllStudySitesAndMap().then(({ data: studySites }) => {
        setAllStudySites(studySites);
      });
    },
    [setAllStudySites]
  );

  useEffect(() => {
    if (allStudySites.length !== 0) {
      const filteredSSus = allStudySites.filter(ssu =>
        filteredStudyTypes.some(type => type === ssu.study.finStudyType)
      );
      setFilteredStudySites(filteredSSus);
    }
  }, [allStudySites, filteredStudyTypes, setFilteredStudySites]);

  useEffect(() => {
    if (studyId && !siteId && allStudySites.length !== 0) {
      const filteredSSU = allStudySites.filter(ssu => studyId === ssu.study.uniqueIdentifier);
      setFilteredStudyTypes([filteredSSU[0].study.finStudyType]);
      setIsManualStudyTypeSelection(false);
    }
  }, [allStudySites, setFilteredStudyTypes, setIsManualStudyTypeSelection, siteId, studyId]);

  useEffect(() => {
    if (studyId && siteId) {
      const filteredSSUs = allStudySites.filter(
        ssu => studyId === ssu.study.uniqueIdentifier && ssu.site.uniqueIdentifier === siteId
      );
      setFilteredStudyTypes([filteredSSUs.map(ssu => ssu.study.finStudyType)[0]]);
      setIsManualStudyTypeSelection(false);
    }
  }, [allStudySites, siteId, studyId, setFilteredStudyTypes, setIsManualStudyTypeSelection]);

  useEffect(() => {
    if (!studyId && !siteId) {
      setFilteredStudyTypes(initialStudyTypes);
      setIsManualStudyTypeSelection(false);
    }
  }, [studyId, siteId, initialStudyTypes, setFilteredStudyTypes, setIsManualStudyTypeSelection]);

  useEffect(() => {
    if (siteId && !studyId && allStudySites.length !== 0 && !isManualStudyTypeSelection) {
      const filteredSSUs = allStudySites.filter(ssu => siteId === ssu.site.uniqueIdentifier);
      const finStudyTypes = uniq(
        filteredSSUs.map(ssu => ssu.study.finStudyType).filter(type => initialStudyTypes.includes(type))
      ).sort((a, b) => initialStudyTypes.indexOf(a) - initialStudyTypes.indexOf(b));
      setFilteredStudyTypes(finStudyTypes);
      setIsManualStudyTypeSelection(false);
    }
  }, [
    allStudySites,
    siteId,
    studyId,
    setFilteredStudyTypes,
    initialStudyTypes,
    setIsManualStudyTypeSelection,
    isManualStudyTypeSelection
  ]);

  const extractNecessaryListFromStudySite = useCallback(
    function(uniqueIdentifier, filterIdentifier, name) {
      const studySiteFilteredByIdentifier = filteredStudySites.filter(
        ssu => !uniqueIdentifier || uniqueIdentifier === ssu[filterIdentifier]
      );
      const list = studySiteFilteredByIdentifier.map(ssu => ssu[name]);
      const uniqueList = uniqBy(list, 'uniqueIdentifier');
      return sortBy(uniqueList, `${name}Name`);
    },
    [filteredStudySites]
  );

  const studies = useMemo(
    function() {
      return extractNecessaryListFromStudySite(siteId, 'siteIdentifier', 'study');
    },
    [extractNecessaryListFromStudySite, siteId]
  );

  const studiesForPcn = useMemo(
    function() {
      return sortBy(
        uniqBy(
          filteredStudySites
            .filter(({ study }) => (studyId ? study.uniqueIdentifier === studyId : true))
            .filter(({ site }) => (siteId ? site.uniqueIdentifier === siteId : true))
            .map(({ study }) => study)
            .filter(study => study.projectCode),
          'uniqueIdentifier'
        ),
        'studyName'
      );
    },
    [studyId, siteId, filteredStudySites]
  );

  const sites = useMemo(
    function() {
      return extractNecessaryListFromStudySite(studyId, 'studyIdentifier', 'site');
    },
    [studyId, extractNecessaryListFromStudySite]
  );
  const initialStudy = useMemo(
    function() {
      return studies.find(({ uniqueIdentifier }) => uniqueIdentifier === studyId);
    },
    [studyId, studies]
  );
  const initialSite = useMemo(
    function() {
      return sites.find(({ uniqueIdentifier }) => uniqueIdentifier === siteId);
    },
    [siteId, sites]
  );

  const studyPcnOrNoPCNMessage = studies => studies.map(study => (study.projectCode ? study.projectCode : 'No PCN'));

  return (
    <>
      <MultiSelectStudyType
        filteredStudyTypes={filteredStudyTypes}
        setFilteredStudyTypes={setFilteredStudyTypes}
        setInitialStudyTypes={setInitialStudyTypes}
        setIsManualStudyTypeSelection={setIsManualStudyTypeSelection}
      />
      <SSUSelect
        handleSiteChange={onSiteChange}
        handleStudyChange={onStudyChange}
        handlePcnChange={study => onPcnChange(study, sites)}
        studies={studies}
        studiesForPcn={studiesForPcn}
        sites={sites}
        initialStudy={initialStudy}
        initialSite={initialSite}
        pcnFlag={pcnFlag}
        customSelectedValueTemplateFunction={studyPcnOrNoPCNMessage}
      />
    </>
  );
}

StudySiteSelector.propTypes = {
  pcnFlag: PropTypes.bool,
  onSiteChange: PropTypes.func,
  onStudyChange: PropTypes.func,
  studyId: PropTypes.string,
  siteId: PropTypes.string
};
